import React, { useState, useMemo, useEffect, ReactElement } from "react";

import {
  TextField,
  Grid,
  InputAdornment,
  Button,
  Typography,
} from "@mui/material";
import {
  AcUnitOutlined,
  BoltOutlined,
  DatasetOutlined,
  EditOutlined,
  ElectricMeterOutlined,
  GasMeterOutlined,
  HeatPumpOutlined,
  KitchenOutlined,
  WbIncandescentOutlined,
  Wifi,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import { useUserContext, useUserContextSetters } from "contexts/UserContext";
import { createEnviroment } from "api/environments";
import { useErrorInfo, useSetError } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";
import {
  CREATE_AC,
  CREATE_ELECTRICAL_PANEL,
  CREATE_LIGHTS,
  CREATE_REFRIGERATION,
  CREATE_ENGINES,
  CREATE_HEATING_WATER,
  CREATE_OTHER_CARGO,
  CREATE_ROUTER,
} from "routers/routePaths";
import { Environment } from "api/automationTypes";
import { WIFI_QUALITY } from "featureSwitches/flagsList";

const ENVIRONMENT_PERIPHERALS: Array<{
  name: string;
  icon: ReactElement;
  linkTo: string;
}> = [
  {
    name: "Ar-condicionado",
    icon: (
      <AcUnitOutlined sx={{ height: "24px", width: "24px", color: "black" }} />
    ),
    linkTo: CREATE_AC,
  },
  {
    name: "Iluminação",
    icon: (
      <WbIncandescentOutlined
        sx={{ height: "24px", width: "24px", color: "black" }}
      />
    ),
    linkTo: CREATE_LIGHTS,
  },
  {
    name: "Qualidade Wi-fi",
    icon: <Wifi sx={{ height: "24px", width: "24px", color: "black" }} />,
    linkTo: WIFI_QUALITY.getValue() ? CREATE_ROUTER : "",
  },
  {
    name: "Quadro elétrico",
    icon: (
      <BoltOutlined sx={{ height: "24px", width: "24px", color: "black" }} />
    ),
    linkTo: CREATE_ELECTRICAL_PANEL,
  },
  {
    name: "Refrigeradores",
    icon: (
      <KitchenOutlined sx={{ height: "24px", width: "24px", color: "black" }} />
    ),
    linkTo: CREATE_REFRIGERATION,
  },
  {
    name: "Aquecedor de água",
    icon: (
      <GasMeterOutlined
        sx={{ height: "24px", width: "24px", color: "black" }}
      />
    ),
    linkTo: CREATE_HEATING_WATER,
  },
  {
    name: "Motores e acionamento",
    icon: (
      <HeatPumpOutlined
        sx={{ height: "24px", width: "24px", color: "black" }}
      />
    ),
    linkTo: CREATE_ENGINES,
  },
  {
    name: "Outras cargas",
    icon: (
      <DatasetOutlined sx={{ height: "24px", width: "24px", color: "black" }} />
    ),
    linkTo: CREATE_OTHER_CARGO,
  },
  {
    name: "Medidores inteligentes",
    icon: (
      <ElectricMeterOutlined
        sx={{ height: "24px", width: "24px", color: "black" }}
      />
    ),
    linkTo: "",
  },
];

const useStyles = makeStyles()((theme) => ({
  item: {
    border: `1px solid ${theme.palette.secondary.light}`,
    borderRadius: "12px",

    "& ~ &": {
      marginTop: theme.spacing(2, 1),
    },
  },
  buttonItem: {
    flexDirection: "column",
    padding: theme.spacing(2),
  },
  buttonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1),
  },
}));

export const CreateEnvironment = ({ location, history }) => {
  const { classes } = useStyles();
  const { token, currentSpace, currentEnvironment } = useUserContext();
  const { setCurrentEnvironment } = useUserContextSetters();
  const setError = useSetError();
  const errorInfo = useErrorInfo();
  const editData: Environment = useMemo(() => {
    return location?.state?.item || {};
  }, [location.state]);
  const [roomName, setRoomName] = useState<string>(editData.name || "");

  useEffect(() => {
    if (editData?.id) {
      setCurrentEnvironment(editData);
    }
  }, [editData, setCurrentEnvironment]);

  const handleSubmit = async () => {
    if (roomName) {
      if (editData.id) {
        // environment is being edited
      } else {
        // creating a new environment
        try {
          await createEnviroment(token, roomName, currentSpace?.id || "");
          history.goBack();
        } catch (error) {
          // @ts-expect-error
          setError(error.response.data.error as ErrorResponse);
        }
      }
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  return (
    <PageStandard
      title={editData.id ? "Edição de ambiente" : "Novo ambiente"}
      displaySelector
    >
      <BlockStandard hideDivider>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            label="Nome"
            sx={{ flex: 3 }}
            value={roomName}
            onChange={({ target }) => setRoomName(target.value)}
            error={!!errorInfo && !roomName}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EditOutlined />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            sx={{ flex: 1, ml: 1 }}
            onClick={handleSubmit}
          >
            Salvar
          </Button>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid item container className={classes.buttonGrid}>
          {ENVIRONMENT_PERIPHERALS.filter((item) => item.linkTo).map(
            ({ name, icon, linkTo }) => (
              <Button
                key={name}
                variant="outlined"
                onClick={() => history.push(linkTo)}
                disabled={!currentEnvironment?.id}
                className={classes.buttonItem}
              >
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="center"
                  sx={{ mb: 1 }}
                >
                  {icon}
                </Grid>
                <Grid item container direction="row" justifyContent="center">
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="center"
                    sx={{ my: 1 }}
                  >
                    {name}
                  </Typography>
                </Grid>
              </Button>
            )
          )}
        </Grid>
      </BlockStandard>
    </PageStandard>
  );
};
